import { useEffect, useState, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../apis/auth/AuthProvider";
import logo from "../../assets/GiantLog-logo.png";
import { getErrorMessage, getUiInterface } from "../../locales/translationUtils";
import AlertOverlay from "../../components/Alert";
import ForgotPasswordModal from "../../components/ForgotPasswordModal";
import { leUsuarioDaSessao } from "../../apis/auth/sessaoDoUsuario";

/**
 * Preferencia do "manter conectado", guardada so para deixar a caixa marcada na
 * proxima visita. Nao guarda nada de credencial - a sessao em si vive no cookie.
 */
const CHAVE_PREFERENCIA_MANTER_CONECTADO = "manterConectado";

const leuPreferenciaManterConectado = (): boolean =>
  localStorage.getItem(CHAVE_PREFERENCIA_MANTER_CONECTADO) === "true";

const Login: React.FC = () => {
  const { loginAction, validateSession } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    rememberMe: leuPreferenciaManterConectado(),
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const usernameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const checkSession = async () => {
      setLoading(true);
      try {
        // Sem perfil guardado nunca houve login neste navegador: nem vale a pena
        // perguntar ao servidor. Se houver, quem decide e o cookie - e o proprio
        // validateSession limpa o perfil quando o cookie ja expirou.
        if (!leUsuarioDaSessao()) {
          setLoading(false);
          return;
        }

        const isValid = await validateSession();
        if (isValid) {
          navigate("/");
        }
      } catch (error) {
        console.error("Sessão inválida, exigindo novo login.");
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, [navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    const loginObj = {
      username: formData.username,
      password: formData.password,
      id: 0,
      rememberMe: formData.rememberMe,
    };

    localStorage.setItem(CHAVE_PREFERENCIA_MANTER_CONECTADO, String(formData.rememberMe));

    try {
      await loginAction(loginObj);

      navigate("/");
    } catch (err: any) {
      let errorMessages: string[] | null = err?.data?.messages;

      if (!errorMessages && err?.message) {
        errorMessages = [err.message];
      }

      setErrorMessage(
        err?.status === 401
          ? getErrorMessage("loginFailedError")
          : `${getErrorMessage("genericError")}: ${errorMessages?.join("; ")}.`
      );

      setShowError(true);
    }
  };

  if (loading) return null;

  return (
    <>
      {showError && errorMessage && (
        <AlertOverlay
          message={errorMessage}
          variant="danger"
          timeInSeconds={5}
          onClose={() => setShowError(false)}
        />
      )}

      <Modal
        show={true}
        backdrop="static"
        keyboard={false}
        centered
        onEntered={() => {
          if (usernameRef.current) {
            usernameRef.current.focus();
          }
        }}
      >
        <Modal.Header className="d-flex justify-content-center align-items-center">
          <img src={logo} alt="Logo" style={{ width: "150px", height: "auto" }} />
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="formLogin">
              <Form.Label>{getUiInterface("username")}:</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={formData.username}
                ref={usernameRef}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group controlId="formPassword" className="mt-3">
              <Form.Label>{getUiInterface("password")}:</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && formData.username.trim()) {
                    handleLogin(e);
                  }
                }}
              />
              <div className="text-end mt-1">
                <Button
                  type="button"
                  variant="link"
                  className="p-0"
                  style={{ fontSize: "13px" }}
                  onClick={() => setShowForgotPassword(true)}
                >
                  {t("forgotPassword.link")}
                </Button>
              </div>
            </Form.Group>
            <Form.Group controlId="formRememberMe" className="mt-3">
              <Form.Check
                type="checkbox"
                label={getUiInterface("rememberMe")}
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleInputChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleLogin}>
            {getUiInterface("login")}
          </Button>
        </Modal.Footer>
      </Modal>

      <ForgotPasswordModal
        show={showForgotPassword}
        onHide={() => setShowForgotPassword(false)}
      />
    </>
  );
};

export default Login;
